import React from "react";
import { motion } from "framer-motion";

const services = [
  {
    title: "Cultural Festivals",
    description:
      "Authentic traditional celebrations that bring communities together in joyous celebration of our heritage.",
    features: [
      "Traditional dance and music performances",
      "Authentic Nepalese cuisine experiences",
      "Cultural workshops and exhibitions",
      "Community engagement activities",
    ],
    image: "/images/tys06577.jpg",
    accentColor: "#C72631",
  },
  {
    title: "Artist Showcases",
    description:
      "Professional platforms for talented Nepalese artists to share their craft with wider audiences.",
    features: [
      "Solo and group performance opportunities",
      "Art exhibition and gallery showcases",
      "Talent development programs",
      "Cultural ambassador initiatives",
    ],
    image: "/images/asc.jpg",
    accentColor: "#C72631",
  },
  {
    title: "Event Management",
    description:
      "Comprehensive planning and execution of cultural events with attention to every detail.",
    features: [
      "End-to-end event planning",
      "Venue coordination and management",
      "Technical production services",
      "Cultural consulting and guidance",
    ],
    image: "/images/asc2.jpg",
    accentColor: "#C72631",
  },
  {
    title: "Community Collaborations",
    description:
      "Building bridges within the global Nepalese community through meaningful partnerships.",
    features: [
      "Cross-cultural exchange programs",
      "Community outreach initiatives",
      "Educational and awareness campaigns",
      "Networking and social events",
    ],
    image: "/images/asc3.jpg",
    accentColor: "#C72631",
  },
];

const stats = [
  { number: "50+", label: "Events Organized" },
  { number: "10K+", label: "Happy Attendees" },
  { number: "100+", label: "Artists Featured" },
  { number: "15+", label: "Cities Reached" },
];

const ServicesPage = () => {
  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="hero-overlay" />
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Our Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Discover how we bring the Nepalese community together through
            vibrant events and unforgettable cultural experiences.
          </motion.p>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <span className="stat-number">{stat.number}</span>
              <span className="stat-label">{stat.label}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-grid-section">
        <div className="section-header">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
          >
            Comprehensive Event Solutions
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            From intimate gatherings to grand celebrations, we handle every
            aspect with excellence
          </motion.p>
        </div>

        <div className="services-grid">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              className="service-card"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              viewport={{ once: true, amount: 0.2 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
            >
              <div className="card-image-container">
                <img src={service.image} alt={service.title} loading="lazy" decoding="async" />
                <div className="card-image-overlay" />
                <div
                  className="card-accent-bar"
                  style={{ background: service.accentColor }}
                />
              </div>
              <div className="card-content">
                <h3>{service.title}</h3>
                <p className="card-description">{service.description}</p>
                <ul className="feature-list">
                  {service.features.map((feature, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + idx * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <span className="check-icon">✓</span>
                      {feature}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="process-container">
          <div className="section-header light">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              Our Simple Process
            </motion.h2>
          </div>

          <div className="process-steps">
            {[
              {
                step: "01",
                title: "Consultation",
                desc: "Share your vision and goals with our team",
              },
              {
                step: "02",
                title: "Planning",
                desc: "We create a detailed event strategy",
              },
              {
                step: "03",
                title: "Execution",
                desc: "Flawless delivery of your cultural event",
              },
              {
                step: "04",
                title: "Celebration",
                desc: "Create lasting memories together",
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                className="process-step"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
              >
                <div className="step-number">{item.step}</div>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-overlay" />
        <motion.div
          className="cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
          >
            Ready to Plan Your Cultural Event?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            Let us help you create an unforgettable experience that celebrates
            your heritage and brings your community together.
          </motion.p>
          <motion.div
            className="cta-buttons"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            <a href="/contact" className="cta-btn primary">
              Get Started Today
              <span className="btn-arrow">→</span>
            </a>
            <a href="/about" className="cta-btn secondary">
              Learn More About Us
            </a>
          </motion.div>
        </motion.div>
      </section>

      <style>{`
        .services-page {
          overflow-x: hidden;
        }

        /* Hero Section */
        .services-hero {
          position: relative;
          min-height: 70vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("/images/bg.jpg") center/cover no-repeat;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          padding: 0 2rem;
        }


        .hero-content h1 {
          font-size: clamp(2.5rem, 6vw, 4.5rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1.5rem;
          line-height: 1.1;
        }

        .hero-content p {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.7;
          max-width: 600px;
          margin: 0 auto;
        }

        /* Stats Section */
        .stats-section {
          background: #fff;
          padding: 0;
          margin-top: -60px;
          position: relative;
          z-index: 10;
        }

        .stats-container {
          max-width: 1000px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .stat-card {
          padding: 2.5rem 1.5rem;
          text-align: center;
          border-right: 1px solid #f0f0f0;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .stat-card:last-child {
          border-right: none;
        }

        .stat-card:hover {
          background: #fafafa;
        }

        .stat-number {
          display: block;
          font-size: 2.5rem;
          font-weight: 900;
          color: #C72631;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          font-size: 0.9rem;
          color: #666;
          font-weight: 500;
        }

        /* Services Grid Section */
        .services-grid-section {
          padding: 6rem 2rem;
          background: #fff;
        }

        .section-header {
          text-align: center;
          max-width: 700px;
          margin: 0 auto 4rem;
        }



        .section-header h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 1rem;
        }

        .section-header.light h2 {
          color: white;
        }

        .section-header p {
          font-size: 1.1rem;
          color: #666;
          line-height: 1.7;
        }

        .services-grid {
          max-width: 1300px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 2.5rem;
        }

        .service-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
          transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
          border: 1px solid #f0f0f0;
        }

        .service-card:hover {
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);
          border-color: transparent;
        }

        .card-image-container {
          position: relative;
          height: 220px;
          overflow: hidden;
        }

        .card-image-container img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .service-card:hover .card-image-container img {
          transform: scale(1.05);
        }

        .card-image-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.2);
        }

        .card-accent-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 4px;
        }

        .card-content {
          padding: 2rem;
        }

        .card-content h3 {
          font-size: 1.4rem;
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 0.75rem;
        }

        .card-description {
          color: #666;
          font-size: 0.95rem;
          line-height: 1.6;
          margin: 0 0 1.5rem;
        }

        .feature-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .feature-list li {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0;
          font-size: 0.9rem;
          color: #555;
          border-bottom: 1px solid #f5f5f5;
        }

        .feature-list li:last-child {
          border-bottom: none;
        }

        .check-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          background: #e8f5e9;
          color: #27ae60;
          border-radius: 50%;
          font-size: 0.75rem;
          font-weight: bold;
          flex-shrink: 0;
        }

        /* Process Section */
        .process-section {
          padding: 6rem 2rem;
          background: #0a0a0a;
        }

        .process-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .process-steps {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2rem;
          margin-top: 4rem;
        }

        .process-step {
          text-align: center;
          padding: 2rem 1.5rem;
          background: rgba(255, 255, 255, 0.04);
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .process-step:hover {
          background: rgba(255, 255, 255, 0.08);
          transform: translateY(-5px);
        }

        .step-number {
          font-size: 2.5rem;
          font-weight: 900;
          color: #C72631;
          margin-bottom: 1rem;
        }

        .process-step h4 {
          color: white;
          font-size: 1.2rem;
          font-weight: 700;
          margin: 0 0 0.75rem;
        }

        .process-step p {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.9rem;
          line-height: 1.5;
          margin: 0;
        }

        /* CTA Section */
        .cta-section {
          position: relative;
          padding: 8rem 2rem;
          background: url("/images/about-2.jpg") center/cover no-repeat;
          text-align: center;
        }

        .cta-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .cta-content {
          position: relative;
          z-index: 2;
          max-width: 700px;
          margin: 0 auto;
        }

        .cta-content h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1.5rem;
        }

        .cta-content p {
          font-size: 1.15rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.7;
          margin: 0 0 2.5rem;
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .cta-btn.primary {
          background: white;
          color: #C72631;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        }

        .cta-btn.primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
        }

        .btn-arrow {
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .cta-btn.primary:hover .btn-arrow {
          transform: translateX(5px);
        }

        .cta-btn.secondary {
          background: transparent;
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.4);
        }

        .cta-btn.secondary:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: white;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .services-grid {
            grid-template-columns: 1fr;
            max-width: 600px;
          }

          .process-steps {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .stats-container {
            grid-template-columns: repeat(2, 1fr);
            margin: 0 1rem;
          }

          .stat-card {
            padding: 2rem 1rem;
          }

          .stat-card:nth-child(2) {
            border-right: none;
          }

          .stat-card:nth-child(1),
          .stat-card:nth-child(2) {
            border-bottom: 1px solid #f0f0f0;
          }

          .services-hero {
            min-height: 50vh;
          }

          .services-grid-section,
          .process-section,
          .cta-section {
            padding: 4rem 1.5rem;
          }

          .card-image-container {
            height: 180px;
          }

          .process-steps {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.5rem;
          }
        }

        @media (max-width: 480px) {
          .stats-container {
            grid-template-columns: 1fr;
          }

          .stat-card {
            border-right: none;
            border-bottom: 1px solid #f0f0f0;
          }

          .stat-card:last-child {
            border-bottom: none;
          }

          .process-steps {
            grid-template-columns: 1fr;
          }

          .cta-buttons {
            flex-direction: column;
          }

          .cta-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default ServicesPage;
